import { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useTooltipRegistry } from "./TooltipContext";

const OFFSET = 10;
const VIEWPORT_PAD = 8;
const ARROW_SIZE = 11;       // visible square side BEFORE 45° rotation
const ENTER_MS = 160;
const EXIT_MS = 120;
const MOVE_MS = 260;         // slide between adjacent tooltip targets
const DEFAULT_DELAY_MS = 300; // can be overridden per-trigger or per-region

// Resolve final position + placement. If preferred side doesn't fit, flip
// to the opposite side when the opposite has room. Always clamps within
// viewport. Arrow offset tracks the trigger's center even after clamping.
const resolvePosition = (triggerRect, tooltipRect, preferredPlace) => {
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  const fits = {
    top:    triggerRect.top    - tooltipRect.height - OFFSET >= VIEWPORT_PAD,
    bottom: triggerRect.bottom + tooltipRect.height + OFFSET <= vh - VIEWPORT_PAD,
    left:   triggerRect.left   - tooltipRect.width  - OFFSET >= VIEWPORT_PAD,
    right:  triggerRect.right  + tooltipRect.width  + OFFSET <= vw - VIEWPORT_PAD,
  };

  const flip = { top: "bottom", bottom: "top", left: "right", right: "left" };
  let place = preferredPlace;
  if (!fits[place] && fits[flip[place]]) place = flip[place];

  let top, left;
  switch (place) {
    case "bottom":
      top  = triggerRect.bottom + OFFSET;
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      break;
    case "left":
      top  = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.left - tooltipRect.width - OFFSET;
      break;
    case "right":
      top  = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.right + OFFSET;
      break;
    default:
      top  = triggerRect.top - tooltipRect.height - OFFSET;
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
  }

  left = Math.max(VIEWPORT_PAD, Math.min(left, vw - tooltipRect.width  - VIEWPORT_PAD));
  top  = Math.max(VIEWPORT_PAD, Math.min(top,  vh - tooltipRect.height - VIEWPORT_PAD));

  let arrowOffset;
  if (place === "top" || place === "bottom") {
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    arrowOffset = Math.max(14, Math.min(triggerCenterX - left, tooltipRect.width - 14));
  } else {
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    arrowOffset = Math.max(14, Math.min(triggerCenterY - top, tooltipRect.height - 14));
  }

  return { top: top + scrollY, left: left + scrollX, place, arrowOffset };
};

const applyArrowPosition = (arrowEl, place, arrowOffset) => {
  if (!arrowEl) return;
  const sz = ARROW_SIZE;
  const out = -Math.round(sz / 2);
  arrowEl.style.top = "";
  arrowEl.style.bottom = "";
  arrowEl.style.left = "";
  arrowEl.style.right = "";
  switch (place) {
    case "bottom":
      arrowEl.style.top = `${out}px`;
      arrowEl.style.left = `${arrowOffset - sz / 2}px`;
      break;
    case "left":
      arrowEl.style.right = `${out}px`;
      arrowEl.style.top = `${arrowOffset - sz / 2}px`;
      break;
    case "right":
      arrowEl.style.left = `${out}px`;
      arrowEl.style.top = `${arrowOffset - sz / 2}px`;
      break;
    default:
      arrowEl.style.bottom = `${out}px`;
      arrowEl.style.left = `${arrowOffset - sz / 2}px`;
  }
};

const transformOriginFor = (place) => {
  switch (place) {
    case "bottom": return "center top";
    case "left":   return "right center";
    case "right":  return "left center";
    default:       return "center bottom";
  }
};

const TooltipPortal = () => {
  const [tooltip, setTooltip] = useState(null);
  const [visible, setVisible] = useState(false);
  const [placeState, setPlaceState] = useState("top");
  const tooltipRef = useRef(null);
  const arrowRef = useRef(null);
  const showTimerRef = useRef(null);
  const exitTimerRef = useRef(null);
  const currentTargetRef = useRef(null);
  const preferredPlaceRef = useRef("top");
  const registry = useTooltipRegistry();

  // Direct DOM update for position. Avoids React state thrash and lets CSS
  // `transition: top, left` animate between old and new positions when the
  // browser has already painted the previous values.
  const updatePosition = useCallback(() => {
    if (!tooltipRef.current || !currentTargetRef.current) return;
    const triggerRect = currentTargetRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const { top, left, place, arrowOffset } = resolvePosition(
      triggerRect,
      tooltipRect,
      preferredPlaceRef.current,
    );
    tooltipRef.current.style.top = `${top}px`;
    tooltipRef.current.style.left = `${left}px`;
    applyArrowPosition(arrowRef.current, place, arrowOffset);
    // Sync the transform-origin (depends on place) when place flips.
    if (place !== placeState) setPlaceState(place);
  }, [placeState]);

  // `continuous` is true when this show fires with no user delay — i.e., the
  // trigger explicitly opts into instant via data-tooltip-delay="0". Only
  // continuous same-kind moves slide; anything that waited for a delay does
  // a clean exit-then-enter so the new tooltip appears at its own position
  // (no perceived slide from "somewhere else").
  const showTooltip = useCallback((target, continuous) => {
    const content = target.getAttribute("data-tooltip-content");
    const tooltipId = target.getAttribute("data-tooltip-id");
    const preferredPlace = target.getAttribute("data-tooltip-place") || "top";
    const richContent = tooltipId && registry[tooltipId];
    const displayContent = richContent || content;
    if (!displayContent) return;

    const wasExitPending = !!exitTimerRef.current;
    clearTimeout(exitTimerRef.current);
    exitTimerRef.current = null;

    const newIsRich = !!richContent;
    const wasMounted = !!tooltipRef.current;
    const wasVisible = visible;
    const sameKind = wasMounted && tooltip && tooltip.isRich === newIsRich;
    currentTargetRef.current = target;
    preferredPlaceRef.current = preferredPlace;

    if (wasMounted && sameKind && continuous) {
      // CONTINUOUS SAME-KIND MOVE: keep mounted, slide to new position via
      // CSS. Defer position by two rAFs so the browser paints the new
      // content at the OLD position first, then transitions to the NEW.
      if (!wasVisible || wasExitPending) setVisible(true);
      setTooltip({ content: displayContent, isRich: newIsRich });
      requestAnimationFrame(() => requestAnimationFrame(updatePosition));
    } else if (wasMounted) {
      // Either cross-kind, or delayed (non-continuous) — exit current, then
      // mount new at its own position. State doesn't swap until the exit
      // animation completes; the useLayoutEffect below positions + enters.
      setVisible(false);
      exitTimerRef.current = setTimeout(() => {
        setTooltip({ content: displayContent, isRich: newIsRich });
      }, EXIT_MS);
    } else {
      // FIRST SHOW. useLayoutEffect below positions and triggers enter.
      setTooltip({ content: displayContent, isRich: newIsRich });
      setVisible(false);
    }
  }, [registry, visible, tooltip, updatePosition]);

  const hideTooltip = useCallback(() => {
    clearTimeout(showTimerRef.current);
    showTimerRef.current = null;
    currentTargetRef.current = null;
    setVisible(false);
    clearTimeout(exitTimerRef.current);
    exitTimerRef.current = setTimeout(() => setTooltip(null), EXIT_MS);
  }, []);

  const findTooltipTarget = useCallback((element) => {
    if (!element || element === document.body) return null;
    if (element.hasAttribute?.("data-tooltip-content") || element.hasAttribute?.("data-tooltip-id")) {
      return element;
    }
    return findTooltipTarget(element.parentElement);
  }, []);

  // First-show positioning: runs once per fresh mount (when wasMounted was
  // false in showTooltip and setVisible(false) was set). Synchronously
  // positions the tooltip while it's still invisible, then triggers the
  // enter animation on the next frame.
  useLayoutEffect(() => {
    if (!tooltip || !tooltipRef.current || !currentTargetRef.current) return;
    if (visible) return;
    updatePosition();
    requestAnimationFrame(() => setVisible(true));
  }, [tooltip, visible, updatePosition]);

  // Re-measure on window resize while visible (cheap; only fires on resize)
  useEffect(() => {
    if (!visible) return;
    const handler = () => updatePosition();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [visible, updatePosition]);

  useEffect(() => {
    const handleMouseOver = (e) => {
      const target = findTooltipTarget(e.target);
      if (!target) return;
      if (currentTargetRef.current === target) return;
      clearTimeout(showTimerRef.current);
      // Delay defaults to DEFAULT_DELAY_MS. A trigger or any ancestor can
      // override via data-tooltip-delay="<ms>" (use 0 for instant show on
      // a specific region like RV column headers).
      let delay = DEFAULT_DELAY_MS;
      let el = target;
      while (el && el !== document.body) {
        const d = el.getAttribute && el.getAttribute("data-tooltip-delay");
        if (d != null) { delay = parseInt(d, 10); if (isNaN(delay)) delay = DEFAULT_DELAY_MS; break; }
        el = el.parentElement;
      }
      if (delay <= 0) {
        showTooltip(target, /* continuous */ true);
      } else {
        showTimerRef.current = setTimeout(() => showTooltip(target, /* continuous */ false), delay);
      }
    };

    const handleMouseOut = (e) => {
      const target = findTooltipTarget(e.target);
      if (!target) return;
      const related = e.relatedTarget;
      // Stay open if mouse moved to a child of the same trigger
      if (related && target.contains(related)) return;
      // Stay open if mouse moved to ANOTHER tooltipped element — the
      // mouseover handler will trigger the move animation.
      if (related && findTooltipTarget(related)) return;
      hideTooltip();
    };

    const handleScroll = (e) => {
      if (tooltipRef.current && tooltipRef.current.contains(e.target)) return;
      if (currentTargetRef.current) hideTooltip();
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") hideTooltip();
    };

    const handleClick = () => {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
      if (currentTargetRef.current) hideTooltip();
    };

    document.addEventListener("mouseover", handleMouseOver, true);
    document.addEventListener("mouseout", handleMouseOut, true);
    document.addEventListener("click", handleClick, true);
    document.addEventListener("scroll", handleScroll, true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", hideTooltip);

    const observer = new MutationObserver(() => {
      const t = currentTargetRef.current;
      if (t && !t.isConnected) hideTooltip();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("mouseover", handleMouseOver, true);
      document.removeEventListener("mouseout", handleMouseOut, true);
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", hideTooltip);
      observer.disconnect();
      clearTimeout(showTimerRef.current);
      clearTimeout(exitTimerRef.current);
    };
  }, [findTooltipTarget, showTooltip, hideTooltip]);

  if (!tooltip) return null;

  const isRich = tooltip.isRich;

  // Theme-aware surface via Tailwind dark / grey variants:
  //   default (light) → white card, slate-200 border, slate-900 text
  //   .dark           → slate-900 card, slate-700 border, slate-100 text
  //   .grey           → zinc-50 card, zinc-300 border, zinc-800 text
  // Arrow uses box-shadow as a 1px outline so its border meets the body's
  // border cleanly at the neck (no visible gap on the body's edge).
  const bodyClasses = [
    "popover-tooltip",
    "rounded-lg",
    "border",
    "bg-white dark:bg-slate-900 grey:bg-zinc-50",
    "border-slate-200 dark:border-slate-700 grey:border-zinc-300",
    "text-slate-900 dark:text-slate-100 grey:text-zinc-800",
    "shadow-2xl shadow-slate-900/15 dark:shadow-black/50 grey:shadow-zinc-900/15",
  ].join(" ");

  const arrowClasses = [
    "absolute pointer-events-none",
    "bg-white dark:bg-slate-900 grey:bg-zinc-50",
    "shadow-[0_0_0_1px_rgba(226,232,240,1)] dark:shadow-[0_0_0_1px_rgba(51,65,85,1)] grey:shadow-[0_0_0_1px_rgba(212,212,216,1)]",
  ].join(" ");

  return createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      className={bodyClasses}
      style={{
        position: "absolute",
        top: -9999,
        left: -9999,
        zIndex: 99999,
        transformOrigin: transformOriginFor(placeState),
        transform: visible ? "scale(1) translateY(0)" : "scale(0.96) translateY(2px)",
        opacity: visible ? 1 : 0,
        transition: visible
          ? `top ${MOVE_MS}ms cubic-bezier(0.32, 0.72, 0, 1), left ${MOVE_MS}ms cubic-bezier(0.32, 0.72, 0, 1), opacity ${ENTER_MS}ms cubic-bezier(0.16, 1, 0.3, 1), transform ${ENTER_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`
          : `opacity ${EXIT_MS}ms cubic-bezier(0.4, 0, 1, 1), transform ${EXIT_MS}ms cubic-bezier(0.4, 0, 1, 1)`,
        padding: isRich ? "10px 12px" : "6px 10px",
        fontSize: isRich ? "12px" : "11px",
        fontFamily: "inherit",
        fontWeight: 500,
        lineHeight: 1.45,
        maxWidth: isRich ? "320px" : "260px",
        whiteSpace: isRich ? "normal" : "pre-wrap",
        wordBreak: "break-word",
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <span
        ref={arrowRef}
        aria-hidden="true"
        className={arrowClasses}
        style={{
          width: `${ARROW_SIZE}px`,
          height: `${ARROW_SIZE}px`,
          transform: "rotate(45deg)",
        }}
      />
      <div style={{ position: "relative" }}>{tooltip.content}</div>
    </div>,
    document.body,
  );
};

export default TooltipPortal;
